import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  useEffect(() => { if (params.get("disabled")) toast.error(t("account.disabled"), { id: "account-disabled" }); }, [params, t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success(t("auth.welcome_toast"));
      navigate(data.establishment_id ? "/app/accueil" : "/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("auth.login_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-6">
      <div className="w-full max-w-md page-enter">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-c flex items-center justify-center text-white font-display text-lg">O</div>
            <span className="font-display text-2xl">Oresto</span>
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="card-c">
          <h1 className="font-display text-3xl">{t("auth.welcome_back")}</h1>
          <p className="text-text-secondary text-sm mt-1">{t("auth.login_subtitle")}</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-text-secondary block mb-2">{t("auth.email")}</label>
              <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-c" />
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary block mb-2">{t("auth.password")}</label>
              <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-c" />
            </div>
            <button data-testid="login-submit-button" disabled={loading} className="btn-primary w-full">
              {loading ? t("auth.logging_in") : t("auth.login_button")}
            </button>
          </form>
          <p className="text-center mt-4">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-text-secondary hover:text-primary-c font-medium">
              {t("auth.forgot_password")}
            </Link>
          </p>
          <p className="text-sm text-text-secondary mt-5 text-center">
            {t("auth.no_account")} <Link to="/register" className="text-primary-c font-semibold" data-testid="login-to-register-link">{t("auth.register_link")}</Link>
          </p>
        </div>
        <nav className="legal-mini-links">
          <Link to="/cgv">{t("footer.terms")}</Link>
          <Link to="/confidentialite">{t("footer.privacy")}</Link>
          <Link to="/mentions-legales">{t("footer.notice")}</Link>
        </nav>
      </div>
    </div>
  );
}
